// Status banner shown at the top of the property detail page. Reads
// the org's freshness thresholds and renders one of four states:
//
//   - fresh    → emerald-tinted card · "Tin còn"        / "cập nhật N ngày trước"
//   - warning  → amber-tinted card   · "Cần xác nhận"   / "N ngày trước"
//   - stale    → deep amber card     · "Đã quá hạn"     / "N ngày trước"
//   - unknown  → neutral card        · "Chưa xác nhận"  / —
//
// Visual language tracks the design source at
// /tmp/design-files/realestate/project/estate-property-detail-v2.jsx
// (FRESHNESS_STATES constant). The entire banner takes the state's
// soft-tint background — the dot + 2-line label + Xác nhận button
// all sit inside that tinted card. The button is white-on-tint when
// fresh (non-urgent) and solid-on-white-text when the listing needs
// attention (warning / stale).

import { toast } from "sonner";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { localizeError } from "@/lib/errorMessages";
import { daysSince, freshnessLevel } from "@/lib/freshness";
import { useConfirmPropertyFresh } from "@/features/property/api";
import { useOrganization } from "@/features/settings/api";

type FreshnessLevel = "fresh" | "warning" | "stale" | "unknown";

interface PropertyFreshnessBannerProps {
  propertyId: number;
  lastFreshnessConfirmedAt: string | null;
  /** Only Owner/Manager and the property's assigned Source may confirm. */
  canConfirm: boolean;
  className?: string;
}

interface FreshnessTone {
  /** Outer card background — a soft tint of the level's hue. */
  card: string;
  /** Outer card border — slightly more saturated than the bg. */
  border: string;
  /** Primary text colour used on the title + detail lines. */
  text: string;
  /** Tailwind opacity layer applied to the detail line under the title. */
  detailMuted: string;
  /** Status dot bg. */
  dot: string;
  /** Halo ring around the dot — same hue as the card bg. */
  dotHalo: string;
  /** Per-state button styling. Fresh = subtle white tile; non-fresh = solid CTA. */
  button: string;
}

const TONES: Record<FreshnessLevel, FreshnessTone> = {
  fresh: {
    card: "bg-emerald-500/10",
    border: "border-emerald-500/25",
    text: "text-emerald-700",
    detailMuted: "text-emerald-700/75",
    dot: "bg-emerald-600",
    dotHalo: "ring-emerald-500/15",
    button:
      "border border-emerald-500/30 bg-card text-emerald-700 hover:bg-emerald-500/5",
  },
  warning: {
    card: "bg-amber-500/10",
    border: "border-amber-500/30",
    text: "text-amber-700",
    detailMuted: "text-amber-700/75",
    dot: "bg-amber-500",
    dotHalo: "ring-amber-500/20",
    button:
      "border border-transparent bg-amber-600 text-white hover:bg-amber-600/90",
  },
  stale: {
    card: "bg-amber-600/12",
    border: "border-amber-700/30",
    text: "text-amber-800",
    detailMuted: "text-amber-800/75",
    dot: "bg-amber-700",
    dotHalo: "ring-amber-700/20",
    button:
      "border border-transparent bg-amber-800 text-white hover:bg-amber-800/90",
  },
  unknown: {
    card: "bg-muted/50",
    border: "border-border",
    text: "text-ink-soft",
    detailMuted: "text-ink-faint",
    dot: "bg-zinc-400",
    dotHalo: "ring-zinc-400/20",
    button:
      "border border-border bg-card text-ink-soft hover:bg-muted",
  },
};

interface FreshnessContent {
  title: string;
  detail: string | null;
  buttonLabel: string;
}

function content(level: FreshnessLevel, age: number | null): FreshnessContent {
  switch (level) {
    case "fresh":
      return {
        title: "Tin còn",
        detail: age !== null ? `cập nhật ${age} ngày trước` : null,
        buttonLabel: "Xác nhận",
      };
    case "warning":
      return {
        title: "Cần xác nhận",
        detail: age !== null ? `${age} ngày trước` : null,
        buttonLabel: "Xác nhận tin còn",
      };
    case "stale":
      return {
        title: "Đã quá hạn",
        detail: age !== null ? `${age} ngày trước` : null,
        buttonLabel: "Xác nhận tin còn",
      };
    case "unknown":
      return {
        title: "Chưa xác nhận",
        detail: null,
        buttonLabel: "Xác nhận tin còn",
      };
  }
}

export function PropertyFreshnessBanner({
  propertyId,
  lastFreshnessConfirmedAt,
  canConfirm,
  className,
}: PropertyFreshnessBannerProps) {
  const orgQuery = useOrganization();
  // Fall back to sensible defaults (7/14) if the org call hasn't resolved
  // yet — better than blocking the banner behind a spinner.
  const freshDays = orgQuery.data?.freshnessFreshDays ?? 7;
  const staleDays = orgQuery.data?.freshnessStaleDays ?? 14;
  const level = freshnessLevel(
    lastFreshnessConfirmedAt,
    freshDays,
    staleDays,
  ) as FreshnessLevel;
  const age = daysSince(lastFreshnessConfirmedAt);
  const tone = TONES[level];
  const copy = content(level, age);

  const confirmMutation = useConfirmPropertyFresh(propertyId);

  const handleConfirm = () => {
    confirmMutation.mutate(undefined, {
      onSuccess: () => toast.success("Đã xác nhận tin còn"),
      onError: (error) => toast.error(localizeError(error)),
    });
  };

  return (
    <section
      aria-label="Trạng thái xác nhận tin"
      className={cn(
        "flex items-center gap-2.5 rounded-xl border px-3 py-2.5",
        tone.card,
        tone.border,
        className,
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          "size-2.5 shrink-0 rounded-full ring-[3px]",
          tone.dot,
          tone.dotHalo,
        )}
      />
      <div className="min-w-0 flex-1">
        <p className={cn("text-[12px] font-bold leading-tight", tone.text)}>
          {copy.title}
        </p>
        {copy.detail !== null ? (
          <p
            className={cn(
              "mt-0.5 font-mono text-[11px] leading-tight",
              tone.detailMuted,
            )}
          >
            {copy.detail}
          </p>
        ) : null}
      </div>
      {canConfirm ? (
        <button
          type="button"
          onClick={handleConfirm}
          disabled={confirmMutation.isPending}
          aria-label="Xác nhận tin còn"
          className={cn(
            "inline-flex h-8 shrink-0 items-center gap-1.5 rounded-lg px-3 text-[12px] font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-60",
            tone.button,
          )}
        >
          <Check aria-hidden="true" className="size-3.5" strokeWidth={2.4} />
          {confirmMutation.isPending ? "Đang lưu..." : copy.buttonLabel}
        </button>
      ) : null}
    </section>
  );
}
